import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api } from "../lib/api";
import { defaultContent } from "../content/defaultContent";

const LanguageContext = createContext(null);

function isObject(v) {
  return v && typeof v === "object" && !Array.isArray(v);
}

// Deep merge: overrides win, but keep default keys that are missing.
function deepMerge(base, override) {
  if (!isObject(base) || !isObject(override)) return override ?? base;
  const out = { ...base };
  for (const key of Object.keys(override)) {
    if (isObject(base[key]) && isObject(override[key])) {
      out[key] = deepMerge(base[key], override[key]);
    } else if (override[key] !== undefined && override[key] !== null) {
      out[key] = override[key];
    }
  }
  return out;
}

const CACHE_PREFIX = "lc_translation_";

export function LanguageProvider({ children }) {
  const [baseContent, setBaseContent] = useState(defaultContent);
  const [lang, setLang] = useState(
    () => localStorage.getItem("lc_lang") || "fr"
  );
  const [content, setContent] = useState(defaultContent);
  const [loading, setLoading] = useState(false);

  // Load admin overrides once, merge with defaults.
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await api.get("/content");
        if (active && data && Object.keys(data).length) {
          const merged = deepMerge(defaultContent, data);
          setBaseContent(merged);
        }
      } catch (e) {
        // fall back to defaults silently
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const translateTo = useCallback(
    async (code) => {
      if (code === "fr") {
        setContent(baseContent);
        return;
      }
      const cacheKey = `${CACHE_PREFIX}${code}`;
      const cachedRaw = localStorage.getItem(cacheKey);
      if (cachedRaw) {
        try {
          setContent(JSON.parse(cachedRaw));
          return;
        } catch (e) {}
      }
      setLoading(true);
      try {
        const { data } = await api.post(`/translate/${code}`, {
          content: baseContent,
        });
        const merged = deepMerge(baseContent, data.content);
        setContent(merged);
        try {
          localStorage.setItem(cacheKey, JSON.stringify(merged));
        } catch (e) {}
      } catch (e) {
        setContent(baseContent);
      } finally {
        setLoading(false);
      }
    },
    [baseContent]
  );

  // Re-run translation when baseContent changes or lang changes.
  useEffect(() => {
    translateTo(lang);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseContent]);

  const changeLang = useCallback(
    async (code) => {
      setLang(code);
      localStorage.setItem("lc_lang", code);
      await translateTo(code);
    },
    [translateTo]
  );

  const value = useMemo(
    () => ({ lang, changeLang, content, loading }),
    [lang, changeLang, content, loading]
  );

  return (
    <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used within LanguageProvider");
  return ctx;
}
